import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import {
  PlusIcon,
  BuildingOffice2Icon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathRoundedSquareIcon,
  PencilIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import { updateUrlWithException, dispatchWithToast } from "../../../../components/Utils";
import { debounce } from "lodash";
import { ModuleFilter } from "../../../../components/utils/ModuleFilter";
import { PMDialog } from "../../../../components/utils/PMDialog";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMField } from "../../../../components/utils/PMField";
import PMSwitch from "../../../../components/utils/PMSwitch";
import { fetchDepartments, createDepartment, updateDepartment } from "../../../../api/department";
import { toast } from "react-hot-toast";
import { useSelector } from "react-redux";
import RoleOnly from "../../../../routes/roleOnly";

export const CompanyDepartmentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const isAdmin = currentUser?.roles?.some((r) => r.name === "admin");

  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchParams] = useSearchParams();
  const [selectedItems, setSelectedItems] = useState([]);
  const [departmentDialogOpen, setDepartmentDialogOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);

  const [controller, setController] = useState({
    filters: [],
    per_page: 25,
    page: searchParams.get("page") ? parseInt(searchParams.get("page")) : 1,
    query: searchParams.get("query") ? searchParams.get("query") : "",
    sort: searchParams.get("sort") ? searchParams.get("sort") : "asc",
    sortBy: searchParams.get("sortBy") ? searchParams.get("sortBy") : "title",
    active: searchParams.get("active") !== null ? searchParams.get("active") === "true" : undefined,
  });

  // Update URL params from controller
  useEffect(() => {
    updateUrlWithException(
      {
        page: controller.page,
        query: controller.query,
        sort: controller.sort,
        sortBy: controller.sortBy,
        active: controller.active,
      },
      "filters"
    );
  }, [controller]);

  const debouncedFetchDepartments = useCallback(
    debounce(async (controller) => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetchDepartments(controller);
        if (response.data) {
          setDepartments(response.data);
        } else {
          setDepartments([]);
        }
      } catch (err) {
        console.error("Error fetching departments:", err);
        setError(err.message || t("Kunne ikke laste avdelinger"));
        toast.error(t("Kunne ikke laste avdelinger"));
      } finally {
        setIsLoading(false);
      }
    }, 250),
    []
  );

  useEffect(() => {
    debouncedFetchDepartments(controller);
    return () => {
      debouncedFetchDepartments.cancel();
    };
  }, [controller, debouncedFetchDepartments]);

  const handleFiltersChange = (filters) => {
    setController({
      ...controller,
      page: 1,
      query: filters.query !== undefined ? filters.query : controller.query,
    });
  };

  const handlePageChange = (newPage) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleCreateDepartment = async (values, helpers) => {
    try {
      const response = await createDepartment(values);
      if (response.errors) {
        helpers.setErrors(response.errors);
        toast.error(t("Kunne ikke opprette avdeling"));
      } else {
        toast.success(t("Avdeling opprettet"));
        setDepartmentDialogOpen(false);
        helpers.resetForm();
        debouncedFetchDepartments(controller);
      }
    } catch (err) {
      console.error("Error creating department:", err);
      toast.error(t("Kunne ikke opprette avdeling"));
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const handleUpdateDepartment = async (values, helpers) => {
    try {
      const response = await updateDepartment({ ...values, id: editingDepartment.id });
      if (response.errors) {
        helpers.setErrors(response.errors);
        toast.error(t("Kunne ikke oppdatere avdeling"));
      } else {
        toast.success(t("Avdeling oppdatert"));
        setDepartmentDialogOpen(false);
        setEditingDepartment(null);
        helpers.resetForm();
        debouncedFetchDepartments(controller);
      }
    } catch (err) {
      console.error("Error updating department:", err);
      toast.error(t("Kunne ikke oppdatere avdeling"));
    } finally {
      helpers.setSubmitting(false);
    }
  };

  const handleEdit = (department) => {
    setEditingDepartment(department);
    setDepartmentDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDepartmentDialogOpen(false);
    setEditingDepartment(null);
  };

  const getInitialFilters = () => {
    return {
      query: controller.query,
    };
  };

  // Check if department is synced (has api_id)
  const isSynced = (department) => {
    return department.api_id != null && department.api_id !== undefined;
  };

  const headerActions = [];

  return (
    <RoleOnly role="admin">
      <DashboardHeader
        subtitle={
          selectedItems.length > 0
            ? `${selectedItems.length} ${selectedItems.length > 1 ? t("avdelinger valgt") : t("avdeling valgt")}`
            : ""
        }
        actions={headerActions}
        title={t("Avdelinger")}
        meta={[]}
        mainButton={
          isAdmin
            ? {
                label: t("Ny avdeling"),
                action: () => setDepartmentDialogOpen(true),
                path: null,
                color: "",
                icon: PlusIcon,
              }
            : null
        }
      >
        <div className="mx-auto mt-8 grid max-w-3xl grid-cols-1 gap-6 sm:px-6 lg:max-w-7xl lg:grid-flow-col-dense lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-3 lg:col-start-1 sm:col-span-3">
            <ModuleFilter
              className={"mt-10"}
              disabled={isLoading}
              onFiltersChange={handleFiltersChange}
              initialFilters={getInitialFilters()}
              searchPlaceholder={t("Søk i avdelinger...")}
              filterTitle={t("Filtrer avdelinger")}
              hideStatusFilter={true}
            />

            {isLoading ? (
              <div className="bg-white shadow sm:rounded-lg p-6">
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <p className="text-red-800">{error}</p>
              </div>
            ) : (
              <div className="bg-white shadow sm:rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {t("Alle avdelinger")} ({departments.length})
                  </h3>

                  {departments.length === 0 ? (
                    <div className="text-center py-12">
                      <BuildingOffice2Icon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">{t("Ingen avdelinger")}</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {t("Kom i gang ved å opprette en ny avdeling.")}
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                      <table className="min-w-full divide-y divide-gray-300">
                        <thead className="bg-gray-50">
                          <tr>
                            <th
                              scope="col"
                              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                            >
                              {t("Navn")}
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              {t("Nummer")}
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              {t("Status")}
                            </th>
                            <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                              {t("Synkronisert")}
                            </th>
                            <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                              <span className="sr-only">{t("Handlinger")}</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {departments.map((department) => (
                            <tr key={department.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                                <div className="flex items-center">
                                  <BuildingOffice2Icon className="h-5 w-5 text-gray-400 mr-2" />
                                  {department.displayTitle || department.title}
                                </div>
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                                {department.number || "-"}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                {department.active ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    <CheckCircleIcon className="h-3 w-3 mr-1" />
                                    {t("Aktiv")}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    <XCircleIcon className="h-3 w-3 mr-1" />
                                    {t("Inaktiv")}
                                  </span>
                                )}
                              </td>
                              <td className="whitespace-nowrap px-3 py-4 text-sm">
                                {isSynced(department) ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    <ArrowPathRoundedSquareIcon className="h-3 w-3 mr-1" />
                                    {t("Synkronisert")}
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {t("Ikke synkronisert")}
                                  </span>
                                )}
                              </td>
                              <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                                <div className="flex items-center justify-end space-x-2">
                                  {isAdmin && (
                                    <button
                                      onClick={() => handleEdit(department)}
                                      className="text-indigo-600 hover:text-indigo-900 inline-flex items-center"
                                    >
                                      <PencilIcon className="h-4 w-4 mr-1" />
                                      {t("Rediger")}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </DashboardHeader>

      {/* Create/Edit Department Dialog */}
      <PMDialog
        open={departmentDialogOpen}
        onClose={handleCloseDialog}
        title={editingDepartment ? t("Rediger avdeling") : t("Ny avdeling")}
        size="lg"
      >
        <Formik
          initialValues={{
            title: editingDepartment?.title || "",
            number: editingDepartment?.number || "",
            active: editingDepartment?.active !== undefined ? editingDepartment.active : true,
          }}
          validationSchema={Yup.object({
            title: Yup.string().required(t("Navn er påkrevd")),
            number: Yup.string(),
            active: Yup.boolean(),
          })}
          onSubmit={editingDepartment ? handleUpdateDepartment : handleCreateDepartment}
          enableReinitialize={true}
        >
          {(formik) => (
            <Form>
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <PMField
                      name="title"
                      type="text"
                      label={t("Navn")}
                      required
                      {...formik}
                    />
                  </div>

                  <div>
                    <PMField
                      name="number"
                      type="text"
                      label={t("Nummer")}
                      {...formik}
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <PMSwitch
                      label={t("Aktiv")}
                      name="active"
                      {...formik}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleCloseDialog}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    {t("Avbryt")}
                  </button>
                  <button
                    type="submit"
                    disabled={formik.isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {formik.isSubmitting
                      ? editingDepartment
                        ? t("Lagrer...")
                        : t("Oppretter...")
                      : editingDepartment
                      ? t("Lagre endringer")
                      : t("Opprett avdeling")}
                  </button>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </PMDialog>
    </RoleOnly>
  );
};

